import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  adminApi,
  type AdminDropboxView,
  type Section,
  type SectionKind,
  type StoredFile,
  type TextEntry,
} from '../lib/api'
import { formatBytes } from '../lib/format'
import { SectionCard } from './SectionCard'
import { FileSection } from './FileSection'
import { LexicalSection } from './LexicalSection'
import { Alert, EmptyState, Spinner } from './ui'
import { shortId } from './DangerZone'
import {
  SectionFields,
  draftFrom,
  draftToInput,
  emptyDraft,
  type SectionDraft,
} from './SectionFields'

/**
 * The section list on the admin page, with the editing controls the owner needs
 * after creation.
 *
 * Sections used to be write-once: what a dropbox asked for was fixed the moment
 * it was created, so a mistyped title or a forgotten section meant deleting the
 * dropbox and starting over — throwing away both links and everything already
 * uploaded. This is the same builder the owner used at /new, applied to a
 * dropbox that already exists.
 *
 * The editing controls live behind a toggle rather than being always on. The
 * page's default use is looking at what arrived, and putting a text field where
 * every section title is would be a form pretending to be a report.
 */
interface Props {
  token: string
  sections: Section[]
  filesBySection: Map<string, StoredFile[]>
  textBySection: Map<string, TextEntry>
  satisfied: Map<string, boolean>
  freeBytes: number
  freeSlots: number
  /**
   * Reports whether a form is open, so the page can pause its background
   * refetch. Must be a stable reference (a useState setter is).
   */
  onBusyChange: (busy: boolean) => void
  onChanged: () => void
}

export function AdminSections({
  token,
  sections,
  filesBySection,
  textBySection,
  satisfied,
  freeBytes,
  freeSlots,
  onBusyChange,
  onChanged,
}: Props) {
  const qc = useQueryClient()
  const key = ['admin-dropbox', token] as const

  const [managing, setManaging] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [creating, setCreating] = useState<SectionKind | null>(null)
  // One value rather than two booleans: there is no such thing as "confirming
  // nothing, but with text typed".
  const [confirming, setConfirming] = useState<{ id: string; typed: string } | null>(null)

  const busy = editingId !== null || creating !== null || confirming !== null
  useEffect(() => onBusyChange(busy), [busy, onBusyChange])

  const settle = () => qc.invalidateQueries({ queryKey: key })

  const create = useMutation({
    mutationFn: (draft: SectionDraft) => adminApi.createSection(token, draftToInput(draft)),
    onSuccess: () => setCreating(null),
    // onSettled rather than onSuccess: after a failure the cache may already
    // disagree with the server, and refetching is how that is discovered.
    onSettled: settle,
  })

  const update = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: SectionDraft }) => {
      const input = draftToInput(draft)
      return adminApi.updateSection(token, id, {
        title: input.title,
        description: input.description,
        required: input.required,
        // Explicit null clears the cap; the field is never simply omitted here,
        // because the form always knows what it wants the cap to be.
        maxFiles: input.kind === 'files' ? input.maxFiles : undefined,
        accept: input.kind === 'files' ? input.accept : undefined,
      })
    },
    onSuccess: () => setEditingId(null),
    onSettled: settle,
  })

  const remove = useMutation({
    mutationFn: ({ id, confirmId }: { id: string; confirmId: string }) =>
      adminApi.deleteSection(token, id, confirmId),
    onSuccess: () => setConfirming(null),
    onSettled: settle,
  })

  const reorder = useMutation({
    // Same-scope mutations run one at a time, in call order. Without this,
    // three quick ↑ clicks are three in-flight requests whose responses can
    // land out of order, and the last response to arrive — not the last click —
    // decides the final order.
    scope: { id: `sections-order-${token}` },
    mutationFn: (ids: string[]) => adminApi.reorderSections(token, ids),
    onMutate: async (ids: string[]) => {
      // Cancelling is what stops an in-flight background refetch from resolving
      // after the optimistic write and stomping it.
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<AdminDropboxView>(key)
      if (prev) qc.setQueryData<AdminDropboxView>(key, { ...prev, sections: applyOrder(prev.sections, ids) })
      return { prev }
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.prev) qc.setQueryData<AdminDropboxView>(key, ctx.prev)
    },
    onSettled: settle,
  })

  const move = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= sections.length) return
    const ids = sections.map((s) => s.id)
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    reorder.mutate(ids)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">Sections</h2>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => {
            setManaging((on) => !on)
            setEditingId(null)
            setCreating(null)
            setConfirming(null)
          }}
        >
          {managing ? 'Done' : 'Edit sections'}
        </button>
      </div>

      {sections.map((section, i) => {
        const held = filesBySection.get(section.id) ?? []
        const note = textBySection.get(section.id)
        const editing = editingId === section.id

        return (
          <SectionCard
            key={section.id}
            index={i + 1}
            title={section.title}
            description={section.description}
            required={section.required}
            satisfied={satisfied.get(section.id) ?? false}
            actions={
              managing && (
                <>
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0 || busy}
                    className="btn-ghost px-2 py-1"
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === sections.length - 1 || busy}
                    className="btn-ghost px-2 py-1"
                    title="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setConfirming(null)
                      setCreating(null)
                      setEditingId(editing ? null : section.id)
                    }}
                    disabled={busy && !editing}
                    className="btn-ghost px-2 py-1"
                  >
                    {editing ? 'Close' : 'Edit'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null)
                      setCreating(null)
                      setConfirming({ id: section.id, typed: '' })
                    }}
                    disabled={sections.length === 1 || (busy && confirming?.id !== section.id)}
                    className="btn-danger px-2 py-1"
                    title={
                      sections.length === 1
                        ? 'A dropbox needs at least one section'
                        : undefined
                    }
                  >
                    Delete
                  </button>
                </>
              )
            }
          >
            {editing && (
              <SectionEditor
                key={section.id}
                initial={draftFrom(section)}
                saving={update.isPending}
                error={update.error}
                onCancel={() => {
                  update.reset()
                  setEditingId(null)
                }}
                onSave={(draft) => update.mutate({ id: section.id, draft })}
              />
            )}

            {confirming?.id === section.id && (
              <DeleteConfirm
                section={section}
                files={held}
                note={note}
                pending={remove.isPending}
                error={remove.error}
                typed={confirming.typed}
                onTyped={(typed) => setConfirming({ id: section.id, typed })}
                onCancel={() => {
                  remove.reset()
                  setConfirming(null)
                }}
                onConfirm={() => remove.mutate({ id: section.id, confirmId: confirming.typed.trim() })}
              />
            )}

            {section.kind === 'files' ? (
              <FileSection
                token={token}
                section={section}
                files={held}
                freeBytes={freeBytes}
                freeSlots={freeSlots}
                readOnly
                isAdmin
                onChanged={onChanged}
              />
            ) : note ? (
              <LexicalSection initialState={note.lexicalJson} readOnly onSave={async () => {}} />
            ) : (
              <EmptyState title="Nothing was written here" />
            )}
          </SectionCard>
        )
      })}

      {managing && (
        <div className="card space-y-4 p-5 sm:p-6">
          {creating ? (
            <SectionEditor
              key={creating}
              initial={emptyDraft(creating)}
              saving={create.isPending}
              error={create.error}
              saveLabel="Add section"
              heading={`New ${creating === 'files' ? 'file upload' : 'text editor'} section`}
              onCancel={() => {
                create.reset()
                setCreating(null)
              }}
              onSave={(draft) => create.mutate(draft)}
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-ghost"
                disabled={busy}
                onClick={() => {
                  setEditingId(null)
                  setConfirming(null)
                  setCreating('files')
                }}
              >
                + File section
              </button>
              <button
                type="button"
                className="btn-ghost"
                disabled={busy}
                onClick={() => {
                  setEditingId(null)
                  setConfirming(null)
                  setCreating('text')
                }}
              >
                + Text section
              </button>
            </div>
          )}
        </div>
      )}

      {reorder.isError && (
        <ProblemAlert title="Could not reorder the sections" error={reorder.error} />
      )}
    </div>
  )
}

/**
 * One section's fields plus Save and Cancel.
 *
 * The draft is seeded from props once and never synced from them again — hence
 * the `key={section.id}` at every call site. The reflexive "keep it in sync"
 * effect is exactly the clobber to avoid: the admin query hands back a new
 * sections array on every fetch, identical bytes or not, and syncing on that
 * would wipe whatever is half-typed.
 */
function SectionEditor({
  initial,
  saving,
  error,
  heading,
  saveLabel = 'Save',
  onSave,
  onCancel,
}: {
  initial: SectionDraft
  saving: boolean
  error: unknown
  heading?: string
  saveLabel?: string
  onSave: (draft: SectionDraft) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState<SectionDraft>(initial)

  return (
    <form
      className="mb-4 space-y-4 rounded-xl border border-zinc-200 p-4 dark:border-zinc-800"
      onSubmit={(e) => {
        e.preventDefault()
        onSave(draft)
      }}
    >
      <div className="text-sm font-semibold">
        {heading ?? `Editing this ${draft.kind === 'files' ? 'file upload' : 'text editor'} section`}
      </div>

      <SectionFields
        value={draft}
        onChange={(changes) => setDraft((d) => ({ ...d, ...changes }))}
        disabled={saving}
        idPrefix={`edit-${initial.kind}-${initial.title || 'new'}`}
      />

      {/* Whether a section takes files or text is fixed once it exists: files
          already live under a files section and a note under a text one, so
          flipping the kind would orphan them. */}
      <p className="text-xs text-zinc-500">
        This section takes {draft.kind === 'files' ? 'files' : 'text'}. That cannot be changed —
        delete it and add a new one instead.
      </p>

      {error !== null && error !== undefined && (
        <ProblemAlert title="Could not save the section" error={error} />
      )}

      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={saving || draft.title.trim() === ''} className="btn-primary">
          {saving && <Spinner />}
          {saveLabel}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className="btn-ghost">
          Cancel
        </button>
      </div>
    </form>
  )
}

/**
 * Typed confirmation for a section delete, on the same bargain DangerZone
 * strikes for the whole dropbox: eight characters is long enough not to come
 * from muscle memory and short enough to retype without pasting, and the server
 * checks it too.
 *
 * The section's short id, not its title: a title can be near-empty, can be
 * duplicated across sections, and is the very field the owner has just been
 * editing.
 */
function DeleteConfirm({
  section,
  files,
  note,
  pending,
  error,
  typed,
  onTyped,
  onConfirm,
  onCancel,
}: {
  section: Section
  files: StoredFile[]
  note: TextEntry | undefined
  pending: boolean
  error: unknown
  typed: string
  onTyped: (value: string) => void
  onConfirm: () => void
  onCancel: () => void
}) {
  const id = shortId(section.id)
  const bytes = files.reduce((n, f) => n + f.sizeBytes, 0)
  const hasNote = (note?.plainText.trim() ?? '') !== ''
  const matches = typed.trim().toLowerCase() === id

  return (
    <div className="mb-4 space-y-3 rounded-xl border border-red-200 p-4 dark:border-red-900/60">
      <Alert tone="error" title="This cannot be undone">
        {files.length > 0 || hasNote ? (
          <>
            Deleting this section also deletes{' '}
            {files.length > 0 && (
              <strong>
                {files.length} file{files.length === 1 ? '' : 's'} ({formatBytes(bytes)})
              </strong>
            )}
            {files.length > 0 && hasNote && ' and '}
            {hasNote && <strong>the note written in it</strong>}. The files leave storage
            permanently — download anything you still need first.
          </>
        ) : (
          <>This section is empty, but removing it changes what the recipient is asked for.</>
        )}
        {section.required && ' It is a required section, so the readiness check changes with it.'}
      </Alert>

      <div>
        <label className="label" htmlFor={`confirm-${section.id}`}>
          Type <code className="font-mono font-bold">{id}</code> to confirm
        </label>
        <input
          id={`confirm-${section.id}`}
          className="input max-w-48 font-mono tracking-widest"
          value={typed}
          onChange={(e) => onTyped(e.target.value)}
          placeholder={id.replace(/./g, '·')}
          maxLength={8}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {error !== null && error !== undefined && (
        <ProblemAlert title="Could not delete the section" error={error} />
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!matches || pending}
          onClick={onConfirm}
          className="btn-danger"
          title={matches ? undefined : 'Type the short id exactly'}
        >
          {pending && <Spinner />}
          Delete section
        </button>
        <button type="button" onClick={onCancel} disabled={pending} className="btn-ghost">
          Cancel
        </button>
      </div>
    </div>
  )
}

/**
 * One renderer for both error shapes the API produces, so a validation failure
 * reads the same here as it does on the builder: a list when the server sent
 * one problem per line, the message otherwise.
 */
function ProblemAlert({ title, error }: { title: string; error: unknown }) {
  const problems =
    error instanceof ApiError && Array.isArray(error.details) ? (error.details as string[]) : null
  return (
    <Alert tone="error" title={title}>
      {problems ? (
        <ul className="list-disc pl-5">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : error instanceof ApiError ? (
        error.message
      ) : (
        'Unexpected error.'
      )}
    </Alert>
  )
}

/**
 * Rebuild the list in the given id order, renumbering position the way the
 * server does. Ids that no longer exist are dropped and sections the caller
 * forgot are appended, so a click against a stale list degrades into a
 * wrong-but-valid order that the invalidate immediately corrects, rather than
 * into a crash.
 */
function applyOrder(sections: Section[], ids: string[]): Section[] {
  const byId = new Map(sections.map((s) => [s.id, s]))
  const out: Section[] = []
  for (const id of ids) {
    const s = byId.get(id)
    if (s) {
      out.push({ ...s, position: out.length })
      byId.delete(id)
    }
  }
  for (const s of byId.values()) out.push({ ...s, position: out.length })
  return out
}
